import { SupplyChainLogisticsActivitiesList } from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import {
  capitalizeFirstLetter,
  formatQuantity,
  formatSmallNumber,
  removeUnderScore,
} from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';

export const supplyChainLogisticsTableColumns: ColumnDef<SupplyChainLogisticsActivitiesList>[] =
  [
    {
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      accessorKey: 'flowType',
      header: ({ column }) => <HeaderCell column={column} name="Type" />,
      cell: ({ getValue }) => {
        const type = getValue<string>();
        return (
          <Text className="text-nowrap">
            {type && capitalizeFirstLetter(type)}
          </Text>
        );
      },
      enableHiding: true,
    },
    {
      accessorKey: 'logisticsFlow',
      accessorFn: (row) => row.logisticsFlow?.name,
      header: ({ column }) => <HeaderCell column={column} name="Flow" />,
      cell: ({ getValue }) => {
        return <Text className="lg:text-nowrap">{getValue<string>()}</Text>;
      },
      enableHiding: true,
    },
    {
      accessorKey: 'amountDistributed',
      header: ({ column }) => (
        <HeaderCell column={column} name="Weight distributed" />
      ),
      cell: ({ row, getValue }) => {
        const weightOption = row.getValue<string>('weightOption');
        const amountDistributed = getValue<number>();

        const isWeightOption = weightOption === 'total_weight';

        return (
          <Text className="w-full min-w-40">
            {isWeightOption
              ? `${formatQuantity(amountDistributed)} tonnes`
              : `${amountDistributed}% of  ${removeUnderScore(weightOption)}`}
          </Text>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: 'coldChainPercentage',
      header: ({ column }) => (
        <HeaderCell column={column} name="% cold-chain" numberColumn />
      ),
      cell: ({ getValue }) => {
        const percentColdChain = getValue<number>();
        return <NumberCell number={percentColdChain} rounded />;
      },
      enableSorting: false,
    },
    {
      accessorKey: 'averageDistance',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Average distance"
          numberColumn
          // unit={<Unit variant="km" />}
        />
      ),
      cell: ({ getValue }) => {
        const distance = Number(getValue<string>());
        return (
          <TextCell
            className="min-w-20 text-right"
            text={formatSmallNumber(distance) + ' km'}
          />
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: 'emissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Total emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissions = getValue<number>();
        return <TextCell text={formatSmallNumber(emissions)} type="number" />;
      },
    },
    {
      accessorKey: 'weightOption',
      enableHiding: true,
    },
  ];
